import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Building2, ArrowRight, CheckCircle2, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchClients, absoluteUrl } from "@/lib/api";

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetchClients()
      .then(setClients)
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, []);

  // Build filter list dynamically from the loaded suppliers
  const filters = useMemo(() => {
    const types = Array.from(new Set(clients.map((c) => c.type)));
    return ["All", ...types];
  }, [clients]);

  const filtered = filter === "All" ? clients : clients.filter((c) => c.type === filter);

  return (
    <div data-testid="clients-page">
      {/* Header */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-emerald-50/60 py-24 md:py-28 overflow-hidden">
        <div aria-hidden className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div aria-hidden className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-3">Our Suppliers</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-slate-900 max-w-3xl mx-auto">
            Nationwide Distributors (Full Provincial Coverage)
          </h1>
          <p className="mt-6 text-slate-600 max-w-2xl mx-auto leading-relaxed">
            These companies have local hubs, sales forces, and logistics networks that deliver to private and public hospitals across Luzon, Visayas, and Mindanao.
          </p>

          {filters.length > 2 && (
            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`client-filter-${f.toLowerCase()}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                    filter === f
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p data-testid="clients-empty" className="text-center text-slate-500 py-16">No clients in this category yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  data-testid={`client-card-${c.id}`}
                  className="group bg-white border border-slate-200 hover:border-blue-200 rounded-2xl p-7 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-start gap-4 mb-5">
                    {c.logo_url ? (
                      <img src={absoluteUrl(c.logo_url)} alt={c.name} className="w-14 h-14 rounded-xl object-cover bg-white border border-slate-100 shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white font-heading font-bold text-lg flex items-center justify-center shrink-0">
                        {c.logo_text}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="font-heading font-semibold text-slate-900 text-lg leading-tight">{c.name}</h3>
                      <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-1.5">
                        <Building2 size={12} /> {c.type}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{c.description}</p>
                  {c.case_study && (
                    <div className="border-t border-slate-100 pt-5">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-emerald-600 mb-2">
                        <Briefcase size={14} /> Case Study
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{c.case_study}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-20 md:py-24 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-8">
          {[
            { stat: "150+", label: "Active retail & wholesale partners" },
            { stat: "12+", label: "Years of trading & distribution" },
            { stat: "100+", label: "Brands across FMCG categories" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <div className="font-heading text-5xl font-semibold text-slate-900">{s.stat}</div>
              <div className="text-sm text-slate-500 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-slate-950 text-white relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(37,99,235,0.18),_transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex justify-center gap-1 mb-5">
            {[...Array(3)].map((_, i) => <CheckCircle2 key={i} size={18} className="text-emerald-400" />)}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            Become our next retail partner
          </h2>
          <p className="mt-5 text-slate-400 max-w-xl mx-auto leading-relaxed">
            Supermarkets, convenience stores, cooperatives, and sub-distributors trust High Roller for reliable FMCG supply. Tell us about your network — a specialist will respond within one business day.
          </p>
          <Link
            to="/#contact"
            data-testid="clients-page-cta"
            className="mt-8 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-3.5 font-medium transition-colors shadow-md"
          >
            Start a Conversation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
